/**
 * cardData adapter — shape contracts
 *
 * Every function in the cardData adapter returns objects with these shapes,
 * regardless of whether the data came from digitalcards.* or the TCGdex API.
 * This keeps consumers (Collection, CardDetail, Scanner, modals) source-agnostic.
 *
 * @typedef {Object} Card
 * @property {string} id                   TCGdex-style ID, e.g. "base1-4"
 * @property {string} set_id               Set ID, e.g. "base1"
 * @property {string} [set_name]           Set name, e.g. "Base Set"
 * @property {string} local_id             Card number within set, e.g. "4"
 * @property {string} name                 Card name
 * @property {string} [category]           "Pokemon" | "Trainer" | "Energy"
 * @property {string} [rarity]
 * @property {number|null} [hp]
 * @property {string[]} [types]
 * @property {string} [stage]
 * @property {string} [illustrator]
 * @property {number[]} [national_pokedex]
 * @property {string} [regulation_mark]
 * @property {object[]} [attacks]
 * @property {object[]} [weaknesses]
 * @property {object[]} [resistances]
 * @property {number} [retreat_cost]
 * @property {object[]|null} [abilities]   NULL in digitalcards for now — consumers must handle
 * @property {string} [description]
 * @property {boolean} [legal_standard]
 * @property {boolean} [legal_expanded]
 * @property {string} image_url            Primary image URL (any CDN — TCGdex, Collectr, Supabase Storage)
 * @property {Variant[]} [variants]        Present when getCard called with { includeVariants: true }
 * @property {"digitalcards"|"tcgdex"} _source   Which backend answered
 *
 * @typedef {Object} Variant
 * @property {string} id                       UUID
 * @property {string} card_id                  FK to Card.id
 * @property {string} type                     "normal" | "holo" | "reverse"
 * @property {string|null} subtype             "shadowless" | "unlimited" | "missing-expansion-symbol" | ...
 * @property {string} size                     "standard" | "jumbo"
 * @property {string[]} stamps                 e.g. ["1st-edition"], ["worlds-2025"]
 * @property {string|null} foil                "cosmos" | "galaxy" | "cracked-ice" | ...
 * @property {string} image_url
 * @property {string|null} brent_variant_name  Friendly name, e.g. "Shadowless 1st Edition Holo"
 * @property {string} source                   "collectr" | "tcgdex" | "ai-generated"
 *
 * @typedef {Object} Set
 * @property {string} id                   TCGdex set ID, e.g. "base1"
 * @property {string} name
 * @property {string} [era_id]
 * @property {string} [abbreviation]
 * @property {string} [serie_id]
 * @property {string|null} [release_date]  ISO date
 * @property {number} [card_count_total]
 * @property {number} [card_count_official]
 * @property {boolean} [is_promo_line]
 * @property {boolean} [is_master_subset]
 * @property {string|null} [parent_set_id]
 * @property {string} [logo_url]
 * @property {Card[]} [cards]              Present when getSet called with { includeCards: true }
 * @property {"digitalcards"|"tcgdex"} _source
 *
 * @typedef {Object} Era
 * @property {string} id
 * @property {string} name
 * @property {number} display_order
 * @property {number|null} [start_year]
 * @property {number|null} [end_year]
 * @property {string} [description]
 *
 * @typedef {Object} LookupOptions
 * @property {string} [language="en"]                   ISO language: en, ja, fr, de, it, es, pt
 * @property {boolean} [includeVariants=false]          Attach card.variants[] on getCard
 * @property {boolean} [includeCards=false]             Attach set.cards[] on getSet
 * @property {"digitalcards"|"tcgdex"} [forceSource]    Override router (test/preview only)
 * @property {"high"|"low"} [resolution="high"]         Image resolution hint (TCGdex only)
 */

// Sentinel export so this module is a valid ES module even though it's purely types.
export const SUPPORTED_LANGUAGES = ['en', 'ja', 'fr', 'de', 'it', 'es', 'pt'];
export const DEFAULT_LANGUAGE = 'en';
